/*
 * Layout  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { LayoutState } from "./LayoutState";
import { TLayoutActivateHandler, TLayoutRenderComponentFn } from "./LayoutTypes";
import { renderLayoutElement } from "./renderLayoutElement";
import { FloatingPanelContextProvider, FloatingPanelProvider } from "../FloatingPanel";
import { FloatingPanelBorder } from "../FloatingPanel/FloatingPanelBorder";
import { ContextMenuProvider } from "../../../contextMenu/ContextMenuProvider";

export interface LayoutProps {
	state: LayoutState;
	renderComponent: TLayoutRenderComponentFn;
	onActivate?: TLayoutActivateHandler;
}

export const Layout: FunctionComponent<LayoutProps> = ({ state, renderComponent, onActivate }) => {
	const rootContent = useMemo(
		() => renderLayoutElement(state.root, renderComponent, onActivate),
		[ state.root, renderComponent, onActivate ]
	);

	const baseClass = BEM("edt-layout");

	const floatingPanelManager = state.getFloatingPanelManager();

	return (
		<FloatingPanelBorder clone>
			<div className={baseClass.toClassName()}>
				<ContextMenuProvider />
				<FloatingPanelContextProvider manager={floatingPanelManager}>
					{rootContent}
				</FloatingPanelContextProvider>
				<FloatingPanelProvider manager={floatingPanelManager} />
			</div>
		</FloatingPanelBorder>
	);
};
